/**
 * @description
 * Platform sync dialog component for syncing users from platform.byteable.ai.
 * Uses Radix UI Dialog pattern with connection status and sync progress.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 6, 2025
 */

import { useState, useEffect } from 'react';
import { X, RefreshCw, AlertCircle, CheckCircle, ExternalLink, Users } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';

interface PlatformSyncDialogProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  onSyncComplete: (result: { added: number; updated: number; removed: number; errors: string[] }) => void;
}

interface PlatformConnectionStatus {
  connected: boolean;
  authenticated: boolean;
  platformUrl?: string;
  userInfo?: {
    email: string;
    role: string;
  };
  lastChecked?: string;
}

interface SyncOptions {
  forceSync: boolean;
  syncApiKeys: boolean;
}

interface SyncResult {
  synced: number;
  added: number;
  updated: number;
  removed: number;
  errors: string[];
}

interface ApiKeySyncResult {
  synced: number;
  errors: string[];
}

/**
 * Platform sync dialog component
 */
export function PlatformSyncDialog({ 
  isOpen, 
  onClose, 
  clientId, 
  onSyncComplete 
}: PlatformSyncDialogProps) {
  const [connectionStatus, setConnectionStatus] = useState<PlatformConnectionStatus>({
    connected: false,
    authenticated: false,
  });
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncOptions, setSyncOptions] = useState<SyncOptions>({
    forceSync: false,
    syncApiKeys: false,
  });
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [apiKeySyncResult, setApiKeySyncResult] = useState<ApiKeySyncResult | null>(null);

  // Check platform connection when modal opens
  useEffect(() => {
    if (isOpen) {
      checkPlatformConnection();
    }
  }, [isOpen]);

  const checkPlatformConnection = async () => {
    try {
      setLoading(true);
      
      // First check if OAuth is configured (doesn't use circuit breaker)
      const configStatus = await api.getPlatformOAuthConfig();
      
      if (!configStatus.success || !configStatus.config.isConfigured) {
        setConnectionStatus({
          connected: false,
          authenticated: false,
          platformUrl: 'https://platform.byteable.ai',
        });
        return;
      }
      
      // If configured, verify the connection
      try {
        const verifyResult = await api.verifyPlatformConnection();
        setConnectionStatus({
          connected: verifyResult.connection.connected,
          authenticated: verifyResult.connection.authenticated,
          platformUrl: verifyResult.connection.platformUrl,
          userInfo: verifyResult.connection.userInfo,
          lastChecked: verifyResult.connection.lastChecked,
        });
      } catch (verifyError) {
        // If verification fails but config exists, show as configured but not connected
        setConnectionStatus({
          connected: false,
          authenticated: false,
          platformUrl: configStatus.config.platformUrl,
          userInfo: configStatus.config.authenticatedUserEmail ? {
            email: configStatus.config.authenticatedUserEmail,
            role: configStatus.config.authenticatedUserRole || 'unknown',
          } : undefined,
        });
      }
    } catch (error) {
      console.error('Error checking platform connection:', error);
      setConnectionStatus({
        connected: false,
        authenticated: false,
        platformUrl: 'https://platform.byteable.ai',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!connectionStatus.connected || !connectionStatus.authenticated) {
      toast.error('Platform connection is not available');
      return;
    }

    setSyncing(true);
    setSyncResult(null);
    setApiKeySyncResult(null);
    
    try {
      const result = await api.syncUsersFromPlatform(clientId, syncOptions);
      setSyncResult(result.syncResult);
      
      // Handle API key sync results if available
      if (result.apiKeySyncResult) {
        setApiKeySyncResult(result.apiKeySyncResult);
      }
      
      onSyncComplete(result);
      
      // Show appropriate success/error messages
      const hasUserErrors = result.syncResult.errors.length > 0;
      const hasApiKeyErrors = result.apiKeySyncResult?.errors.length > 0;
      
      if (hasUserErrors || hasApiKeyErrors) {
        const errorCount = result.syncResult.errors.length + (result.apiKeySyncResult?.errors.length || 0);
        toast.error(`Sync completed with ${errorCount} errors`);
      } else {
        const apiKeyMessage = result.apiKeySyncResult?.synced 
          ? `, API keys synced: ${result.apiKeySyncResult.synced}` 
          : '';
        toast.success(`Sync completed successfully! Added: ${result.syncResult.added}, Updated: ${result.syncResult.updated}, Removed: ${result.syncResult.removed}${apiKeyMessage}`);
      }
    } catch (error) {
      console.error('Error syncing users:', error);
      toast.error(`Failed to sync users: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleClose = () => {
    setSyncResult(null);
    setApiKeySyncResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={handleClose} />
      
      <div className="relative w-full max-w-2xl rounded-lg border bg-card shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-card-foreground">
              Sync Users from Platform
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            disabled={syncing}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Platform Connection Status */}
          <div>
            <h3 className="text-md font-medium text-card-foreground mb-3">
              Platform Connection Status
            </h3>
            
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <span className="text-sm text-muted-foreground">Checking connection...</span>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  {connectionStatus.connected && connectionStatus.authenticated ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-destructive" />
                  )}
                  <span className={`text-sm font-medium ${
                    connectionStatus.connected && connectionStatus.authenticated 
                      ? 'text-green-600' 
                      : 'text-destructive'
                  }`}>
                    {connectionStatus.connected && connectionStatus.authenticated 
                      ? 'Connected & Authenticated' 
                      : 'Disconnected / Unauthenticated'
                    }
                  </span>
                </div>
                
                {connectionStatus.platformUrl && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Platform URL:</span>
                    <a 
                      href={connectionStatus.platformUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 flex items-center gap-1"
                    >
                      <span>{connectionStatus.platformUrl}</span>
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
                
                {connectionStatus.userInfo && (
                  <div className="text-sm text-muted-foreground">
                    Authenticated as: <span className="font-medium">{connectionStatus.userInfo.email}</span> ({connectionStatus.userInfo.role})
                  </div>
                )}
                
                <button
                  onClick={checkPlatformConnection}
                  disabled={loading}
                  className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
                >
                  <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
                  <span>Recheck Connection</span>
                </button>
              </div>
            )}
          </div>

          {/* Sync Options */}
          <div>
            <h3 className="text-md font-medium text-card-foreground mb-3">
              Sync Options
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="forceSync"
                  checked={syncOptions.forceSync}
                  onChange={(e) => setSyncOptions(prev => ({ ...prev, forceSync: e.target.checked }))}
                  disabled={syncing}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-ring focus:ring-2"
                />
                <label htmlFor="forceSync" className="text-sm text-card-foreground">
                  Force sync (overwrite existing data)
                </label>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="syncApiKeys"
                  checked={syncOptions.syncApiKeys}
                  onChange={(e) => setSyncOptions(prev => ({ ...prev, syncApiKeys: e.target.checked }))}
                  disabled={syncing}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-ring focus:ring-2"
                />
                <label htmlFor="syncApiKeys" className="text-sm text-card-foreground">
                  Sync API keys from platform metadata
                </label>
              </div>
            </div>
          </div>

          {/* Sync Results */}
          {syncResult && (
            <div>
              <h3 className="text-md font-medium text-card-foreground mb-3">
                Sync Results
              </h3>
              
              <div className="bg-muted rounded-lg p-4 space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-medium text-foreground">{syncResult.synced}</div>
                    <div className="text-muted-foreground">Synced</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-green-600">{syncResult.added}</div>
                    <div className="text-muted-foreground">Added</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-primary">{syncResult.updated}</div>
                    <div className="text-muted-foreground">Updated</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-destructive">{syncResult.removed}</div>
                    <div className="text-muted-foreground">Removed</div>
                  </div>
                </div>
                
                {syncResult.errors.length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm font-medium text-destructive mb-2">
                      Errors ({syncResult.errors.length}):
                    </div>
                    <div className="space-y-1">
                      {syncResult.errors.map((error, index) => (
                        <div key={index} className="text-sm text-destructive bg-destructive/10 p-2 rounded">
                          {error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* API Key Sync Results */}
          {apiKeySyncResult && (
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <h3 className="text-sm font-medium text-card-foreground">API Key Sync Results</h3>
              </div>
              <div className="space-y-2">
                <div className="text-sm text-card-foreground">
                  <span className="font-medium">API Keys synced:</span> {apiKeySyncResult.synced}
                </div>
                {apiKeySyncResult.errors.length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-destructive mb-2">
                      API Key Errors ({apiKeySyncResult.errors.length}):
                    </div>
                    <div className="space-y-1">
                      {apiKeySyncResult.errors.map((error, index) => (
                        <div key={index} className="text-sm text-destructive bg-destructive/10 p-2 rounded">
                          {error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={handleClose}
              disabled={syncing}
              className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            >
              Close
            </button>
            <button
              onClick={handleSync}
              disabled={syncing || !connectionStatus.connected || !connectionStatus.authenticated}
              className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 flex items-center gap-2"
            >
              {syncing ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                  <span>Syncing...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  <span>Start Sync</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
